import React, { useContext, useEffect, useRef, useState } from "react"
import { Animated, Easing, View } from "react-native"
import TextM from "@common/components/text/TextM"
import FilledButtonM from "@common/components/button/FilledButtonM"
import TouchableRippleM from "@common/components/button/TouchableRippleM"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { activitiesService } from "../../../data/services/activitiesService"
import { FACTS, VIRELANGUES } from "../../../engine/activity/correction/correctionPauses"

/**
 * « En attendant… » — ce que l'élève a sous les yeux pendant que Sam corrige sa
 * copie.
 *
 * Une seule forme par copie, tirée une fois : le « Défi éclair » dès qu'il y a
 * des questions pour l'élève, sinon l'une des trois pauses (respiration,
 * vire-langue, anecdote). Le défi ne compte pas de points : seulement le feu
 * des bonnes réponses d'affilée, et un score à la fin. Chaque réponse part au
 * serveur, qui s'en sert pour ne plus reposer ce que l'élève sait. Sa limite
 * est celle de l'attente : il disparaît avec elle.
 */
const LETTERS = ["A", "B", "C", "D", "E", "F"]
const BREAKS = ["breathing", "tongue", "fact"]

// Enchaîne en boucle les éléments d'une liste (vire-langues, anecdotes).
const useCycle = (list) => {
    const [i, setI] = useState(0)
    return [list[i % list.length], () => setI((v) => v + 1)]
}

// Sur un petit écran, tout rétrécit pour que la carte tienne sans défiler.
const useScale = () => {
    const dimensions = useContext(DimensionsContext)
    const width = dimensions?.width ?? 400
    const height = dimensions?.height ?? 780
    const scale = Math.min(Math.max(Math.min(width / 450, height / 800), 0.8), 1.15)
    return (n) => Math.round(n * scale)
}

// Rendu d'un texte avec segments **gras**.
const RichText = ({ text, style, color, fontSize = 16 }) => {
    const parts = `${text ?? ""}`.split(/(\*\*[^*]+\*\*)/g).filter(Boolean)
    return (
        <TextM wrap fontSize={fontSize} fontWeight="SemiBold" style={style} color={color}>
            {parts.map((p, i) =>
                p.startsWith("**") && p.endsWith("**") ? (
                    <TextM key={i} fontWeight="ExtraBold" fontSize={fontSize} color={theme.colors.primary}>
                        {p.slice(2, -2)}
                    </TextM>
                ) : (
                    p
                )
            )}
        </TextM>
    )
}

const Eyebrow = ({ children, sz }) => (
    <TextM
        fontWeight="ExtraBold"
        fontSize={sz(12)}
        color={theme.colors.primary}
        style={{ letterSpacing: 1, textTransform: "uppercase" }}
    >
        {children}
    </TextM>
)

// Le bouton de l'app, à la largeur de la carte.
const NextButton = ({ label, onPress, sz }) => (
    <FilledButtonM
        label={label}
        onPress={onPress}
        color={theme.colors.primary}
        style={{ width: "100%", marginTop: sz(14), marginBottom: 0 }}
    />
)

// ── Le défi : une série de questions, et le feu des bonnes réponses d'affilée ──
const NEW_GAME = { index: 0, selected: null, streak: 0, bestStreak: 0, right: 0 }

const QuizChallenge = ({ questions, activityId, sz }) => {
    const [game, setGame] = useState(NEW_GAME)
    const { index, selected, streak, bestStreak, right } = game

    // Chaque question entre en fondu ; le feu rebondit quand la série s'allonge.
    const fade = useRef(new Animated.Value(1)).current
    const streakScale = useRef(new Animated.Value(1)).current
    useEffect(() => {
        fade.setValue(0)
        Animated.timing(fade, { toValue: 1, duration: 300, easing: Easing.out(Easing.quad), useNativeDriver: true }).start()
    }, [index, fade])

    if (index >= questions.length) {
        return (
            <Animated.View style={{ alignItems: "center", opacity: fade }}>
                <Eyebrow sz={sz}>Défi terminé</Eyebrow>
                <TextM
                    fontWeight="ExtraBold"
                    fontSize={sz(28)}
                    color={theme.colors.primaryDark}
                    style={{ marginTop: sz(10) }}
                >
                    {right} sur {questions.length}
                </TextM>
                {bestStreak >= 2 && (
                    <TextM
                        fontWeight="Bold"
                        fontSize={sz(14)}
                        color={theme.colors.grey}
                        style={{ marginTop: sz(6), textAlign: "center" }}
                        wrap
                    >
                        Meilleure série 🔥 x{bestStreak}
                    </TextM>
                )}
                <NextButton label="Rejouer" onPress={() => setGame(NEW_GAME)} sz={sz} />
            </Animated.View>
        )
    }

    const q = questions[index]
    const answered = selected !== null
    const isRight = selected === q.correct_index

    const onSelect = (i) => {
        // Sans attendre la réponse du serveur : le jeu ne dépend pas de lui.
        if (q.id) {
            activitiesService
                .answerCorrectionWait({ activityId }, { quiz_question_id: q.id, answer: q.choices[i] })
                .catch(() => {})
        }
        if (i !== q.correct_index) {
            setGame((g) => ({ ...g, selected: i, streak: 0 }))
            return
        }
        setGame((g) => ({
            ...g,
            selected: i,
            streak: g.streak + 1,
            bestStreak: Math.max(g.bestStreak, g.streak + 1),
            right: g.right + 1,
        }))
        Animated.sequence([
            Animated.spring(streakScale, { toValue: 1.35, useNativeDriver: true, friction: 4 }),
            Animated.spring(streakScale, { toValue: 1, useNativeDriver: true, friction: 4 }),
        ]).start()
    }
    const onNext = () => setGame((g) => ({ ...g, selected: null, index: g.index + 1 }))

    const badgeSize = sz(26)

    return (
        <View>
            <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: sz(12), minHeight: sz(28) }}>
                <View style={{ flexDirection: "row", alignItems: "center" }}>
                    <Eyebrow sz={sz}>Défi éclair</Eyebrow>
                    <TextM fontWeight="Bold" fontSize={sz(12)} color={theme.colors.grey} style={{ marginLeft: 8 }}>
                        {index + 1} / {questions.length}
                    </TextM>
                </View>
                {streak >= 2 && (
                    <Animated.View
                        style={{
                            borderRadius: 999,
                            paddingVertical: 4,
                            paddingHorizontal: 10,
                            backgroundColor: theme.colors.white,
                            transform: [{ scale: streakScale }],
                        }}
                    >
                        <TextM fontWeight="ExtraBold" fontSize={sz(13)} color={theme.colors.warning}>
                            🔥 x{streak}
                        </TextM>
                    </Animated.View>
                )}
            </View>

            <Animated.View style={{ opacity: fade }}>
                <TextM
                    fontWeight="ExtraBold"
                    fontSize={sz(18)}
                    wrap
                    color={theme.colors.primaryDark}
                    style={{ textAlign: "center", marginBottom: sz(12) }}
                >
                    {q.question}
                </TextM>

                <View style={{ gap: sz(8) }}>
                    {q.choices.map((choice, i) => {
                        const isCorrect = i === q.correct_index
                        const isSelected = i === selected
                        let bg = theme.colors.white
                        let badgeBg = theme.colors.primary
                        let textColor = theme.colors.primaryDark
                        let mark = null

                        if (answered && isCorrect) {
                            bg = theme.colors.successLight
                            badgeBg = theme.colors.success
                            textColor = theme.colors.successDark
                            mark = "✓"
                        } else if (answered && isSelected) {
                            bg = theme.colors.errorLight
                            badgeBg = theme.colors.error
                            textColor = theme.colors.error
                            mark = "✗"
                        }

                        return (
                            <TouchableRippleM
                                key={i}
                                onPress={answered ? undefined : () => onSelect(i)}
                                style={{ borderRadius: 14, backgroundColor: bg, paddingVertical: sz(10), paddingHorizontal: sz(13) }}
                            >
                                <View style={{ flexDirection: "row", alignItems: "center", width: "100%" }}>
                                    <View
                                        style={{
                                            width: badgeSize,
                                            height: badgeSize,
                                            borderRadius: 99,
                                            backgroundColor: badgeBg,
                                            alignItems: "center",
                                            justifyContent: "center",
                                            marginRight: sz(10),
                                        }}
                                    >
                                        <TextM fontWeight="ExtraBold" fontSize={sz(13)} color={theme.colors.white}>
                                            {LETTERS[i] ?? "?"}
                                        </TextM>
                                    </View>
                                    <TextM fontWeight="SemiBold" fontSize={sz(15)} color={textColor} wrap style={{ flex: 1 }}>
                                        {choice}
                                    </TextM>
                                    {mark && (
                                        <TextM fontWeight="ExtraBold" fontSize={sz(18)} color={textColor} style={{ marginLeft: 8 }}>
                                            {mark}
                                        </TextM>
                                    )}
                                </View>
                            </TouchableRippleM>
                        )
                    })}
                </View>
            </Animated.View>

            {answered && (
                <>
                    <TextM
                        fontWeight="Bold"
                        fontSize={sz(14)}
                        color={isRight ? theme.colors.successDark : theme.colors.grey}
                        style={{ textAlign: "center", marginTop: sz(12) }}
                        wrap
                    >
                        {isRight ? "Bien joué !" : "Pas tout à fait : la bonne réponse est en vert."}
                    </TextM>
                    <NextButton
                        label={index + 1 < questions.length ? "Question suivante" : "Voir mon score"}
                        onPress={onNext}
                        sz={sz}
                    />
                </>
            )}
        </View>
    )
}

// ── Pause : respiration ──
const BreathingCard = ({ sz }) => {
    const scale = useRef(new Animated.Value(0.9)).current
    const [phase, setPhase] = useState("Inspire…")

    useEffect(() => {
        const loop = Animated.loop(
            Animated.sequence([
                Animated.timing(scale, { toValue: 1.1, duration: 2200, easing: Easing.inOut(Easing.quad), useNativeDriver: true }),
                Animated.timing(scale, { toValue: 0.9, duration: 2200, easing: Easing.inOut(Easing.quad), useNativeDriver: true }),
            ])
        )
        loop.start()
        const id = setInterval(() => setPhase((p) => (p === "Inspire…" ? "Expire…" : "Inspire…")), 2200)
        return () => {
            loop.stop()
            clearInterval(id)
        }
    }, [scale])

    const bubble = sz(130)

    return (
        <View style={{ alignItems: "center" }}>
            <Eyebrow sz={sz}>Pause zen</Eyebrow>
            <Animated.View
                style={{
                    transform: [{ scale }],
                    marginTop: sz(16),
                    width: bubble,
                    height: bubble,
                    borderRadius: 99,
                    backgroundColor: theme.colors.white,
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <TextM fontSize={sz(48)}>🌿</TextM>
            </Animated.View>
            <TextM fontWeight="ExtraBold" fontSize={sz(22)} color={theme.colors.primaryDark} style={{ marginTop: sz(20) }}>
                {phase}
            </TextM>
            <TextM fontWeight="SemiBold" fontSize={sz(14)} color={theme.colors.grey} style={{ marginTop: sz(6) }}>
                Suis la bulle et respire
            </TextM>
        </View>
    )
}

// ── Pause : vire-langue ──
const TongueCard = ({ sz }) => {
    const [phrase, next] = useCycle(VIRELANGUES)
    return (
        <View style={{ alignItems: "center" }}>
            <Eyebrow sz={sz}>Vire-langue</Eyebrow>
            <TextM fontWeight="SemiBold" fontSize={sz(13)} color={theme.colors.grey} style={{ marginTop: sz(6), marginBottom: sz(12) }}>
                Répète à voix haute
            </TextM>
            <TextM
                fontWeight="Bold"
                fontSize={sz(19)}
                wrap
                color={theme.colors.primaryDark}
                style={{ textAlign: "center", lineHeight: sz(27) }}
            >
                « {phrase} »
            </TextM>
            <NextButton label="Un autre" onPress={next} sz={sz} />
        </View>
    )
}

// ── Pause : anecdote, blague ou citation ──
const FactCard = ({ sz }) => {
    const [item, next] = useCycle(FACTS)
    return (
        <View style={{ alignItems: "center" }}>
            <Eyebrow sz={sz}>{item.label}</Eyebrow>
            <TextM fontSize={sz(36)} style={{ marginVertical: sz(8) }}>
                {item.emoji}
            </TextM>
            <RichText text={item.text} color={theme.colors.primaryDark} fontSize={sz(17)} style={{ textAlign: "center", lineHeight: sz(25) }} />
            <NextButton label="Encore" onPress={next} sz={sz} />
        </View>
    )
}

const PendingAction = ({ questions, activityId }) => {
    const sz = useScale()
    const series = questions ?? []

    // Le défi d'abord ; une pause quand aucune question ne correspond à l'élève.
    const [form] = useState(() =>
        series.length ? "quiz" : BREAKS[Math.floor(Math.random() * BREAKS.length)]
    )

    switch (form) {
        case "quiz":
            return <QuizChallenge questions={series} activityId={activityId} sz={sz} />
        case "tongue":
            return <TongueCard sz={sz} />
        case "fact":
            return <FactCard sz={sz} />
        default:
            return <BreathingCard sz={sz} />
    }
}

// Les questions ne changent pas pendant l'attente : inutile de suivre les
// rendus de l'écran, qui en fait un à chaque relevé de la correction.
export default React.memo(PendingAction)
